import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PageBody, PageTitle, Paper, SaveChangesBar, SettingsNavigation } from 'src/components';
import { useQueueStatsQuery } from 'src/api/queries/queueQueries';
import { QueueStats } from 'src/api/functions/queueApi';
import useRoleManager from 'src/hooks/useRoleManager';
import { SlidingQueueChart } from './SlidingQueueChart';
import styles from './styles.module.scss';

const REFRESH_INTERVAL = 10000; // 10 seconds
const HISTORY_DURATION = 60000; // 60 seconds in milliseconds

// Queue name mapping for display names
const QUEUE_DISPLAY_NAMES: Record<string, string> = {
  'ASSET_UPLOADS_IN_PROGRESS': 'Asset Uploads',
  'WEB_CALLBACKS_IN_PROGRESS': 'Web Callbacks',
  'IMAGE_PROCESSING_IN_PROGRESS': 'Image Processing',
  'DEEPZOOM_IMAGE_PROCESSING_IN_PROGRESS': 'Advanced Image Processing',
  'VIDEO_TRANSCODES_IN_PROGRESS': 'Video Transcodes',
  'DB_EXPORT_IN_PROGRESS': 'Database Export',
  'ASSET_POST_PROCESSING_IN_PROGRESS': 'Asset Post Processing',
};

type QueueHistoryData = {
  [queueName: string]: Array<{ timestamp: number; size: number }>;
};

export const ProcessingQueues = () => {
  const location = useLocation();
  const { isAdmin } = useRoleManager();
  const isSettingsRoute = location.pathname.includes('/admin/settings/processing-queues');
  
  const { data: queueStats, isLoading, error, dataUpdatedAt } = useQueueStatsQuery(
    {},
    { 
      refetchInterval: REFRESH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    }
  );
  const [timeRemaining, setTimeRemaining] = useState(REFRESH_INTERVAL / 1000);
  const [queueHistory, setQueueHistory] = useState<QueueHistoryData>({});
  const lastAddedSizeRef = React.useRef<Record<string, number>>({});

  // Store historical queue data (last 60 seconds)
  // Only add new points when we actually get new data from the API
  useEffect(() => {
    if (!queueStats || queueStats.length === 0) return;

    const now = Date.now();
    
    // First, check which queues need new points and update refs immediately
    const queuesToAdd: Array<{ name: string; size: number }> = [];
    queueStats.forEach((queue: QueueStats) => {
      const lastAddedSize = lastAddedSizeRef.current[queue.name];
      // Only add if size is different from what we last added
      if (lastAddedSize === undefined || lastAddedSize !== queue.size) {
        queuesToAdd.push({ name: queue.name, size: queue.size });
        // Update ref immediately to prevent duplicate processing
        lastAddedSizeRef.current[queue.name] = queue.size;
      }
    });

    // Only update state if there are new points to add
    if (queuesToAdd.length > 0) {
      setQueueHistory((prevHistory) => {
        const newHistory: QueueHistoryData = { ...prevHistory };

        queuesToAdd.forEach(({ name, size }) => {
          if (!newHistory[name]) {
            newHistory[name] = [];
          }

          // Add new data point
          newHistory[name].push({
            timestamp: now,
            size: size,
          });

          // Remove data points older than 60 seconds
          newHistory[name] = newHistory[name].filter(
            (point) => now - point.timestamp <= HISTORY_DURATION
          );

          // Deduplicate: remove consecutive points with the same size (safety net)
          const deduplicated = [];
          for (let i = 0; i < newHistory[name].length; i++) {
            const current = newHistory[name][i];
            const previous = deduplicated[deduplicated.length - 1];
            if (!previous || previous.size !== current.size) {
              deduplicated.push(current);
            }
          }
          newHistory[name] = deduplicated;

          // Keep only the last 6 data points (60 seconds / 10 seconds = 6 points)
          if (newHistory[name].length > 6) {
            newHistory[name] = newHistory[name].slice(-6);
          }
        });

        return newHistory;
      });
    }
  }, [queueStats]);

  // Countdown timer - reset when data updates
  useEffect(() => {
    if (isLoading) {
      setTimeRemaining(REFRESH_INTERVAL / 1000);
      return;
    }

    // Reset timer when dataUpdatedAt changes (new data arrived from refetch)
    setTimeRemaining(REFRESH_INTERVAL / 1000);
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, REFRESH_INTERVAL - elapsed);
      
      setTimeRemaining(Math.ceil(remaining / 1000));
      
      if (remaining <= 0) {
        clearInterval(interval);
        setTimeRemaining(0);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isLoading, dataUpdatedAt]);

  // Check if user is admin
  if (!isAdmin) {
    return (
      <div className={styles.pageWrapper}>
        {isSettingsRoute && <SettingsNavigation />}
        {isSettingsRoute && <SaveChangesBar />}
        {!isSettingsRoute && <PageTitle title="Workflows & Tasks" className={styles.pageTitle} />}
        <PageBody>
          <div className={styles.error}>
            Access denied. This feature is only available to administrators.
          </div>
        </PageBody>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        {isSettingsRoute && <SettingsNavigation />}
        {isSettingsRoute && <SaveChangesBar />}
        {!isSettingsRoute && <PageTitle title="Workflows & Tasks" className={styles.pageTitle} />}
        <PageBody>
          <div className={styles.loading}>Loading data...</div>
        </PageBody>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageWrapper}>
        {isSettingsRoute && <SettingsNavigation />}
        {isSettingsRoute && <SaveChangesBar />}
        {!isSettingsRoute && <PageTitle title="Workflows & Tasks" className={styles.pageTitle} />}
        <PageBody>
          <div className={styles.error}>
            Failed to load queue statistics. Please try again later.
          </div>
        </PageBody>
      </div>
    );
  }

  const queues = queueStats || [];

  return (
    <div className={styles.pageWrapper}>
      {isSettingsRoute && <SettingsNavigation />}
      {isSettingsRoute && <SaveChangesBar />}
      {!isSettingsRoute && <PageTitle title="Workflows & Tasks" className={styles.pageTitle} />}
      <PageBody>
        {!isSettingsRoute && (
          <>
            <p className={styles.description}>
              These charts provide data on number of tasks processed per minute for your account.
            </p>
            <p className={styles.infoAlert}>
              A value of 0 (zero) indicates all tasks were completed at the time of data refresh.
            </p>
            <div className={styles.refreshIndicator}>
              <div className={styles.queueSizeLabel}>Last 60 seconds</div>
              <div className={styles.refreshSection}>
                <div className={styles.refreshLabel}>Auto-refreshing in:</div>
                <div className={styles.countdownText}>{timeRemaining}s</div>
              </div>
            </div>
          </>
        )}
        <div className={styles.content}>
          {queues.length === 0 ? (
            <Paper className={styles.emptyState}>
              <p>No queue statistics available.</p>
            </Paper>
          ) : (
            <div className={styles.queuesList}>
              {queues.map((queue: QueueStats) => (
                <Paper key={queue.name} className={styles.queueCard}>
                  <div className={styles.queueHeader}>
                    <h3 className={styles.queueTitle}>
                      {QUEUE_DISPLAY_NAMES[queue.name] || queue.name}
                    </h3>
                  </div>

                  <div className={styles.queueStats}>
                    <div className={styles.chartContainer}>
                      <SlidingQueueChart
                        data={queueHistory[queue.name] || []}
                        queueName={queue.name}
                      />
                    </div>
                  </div>
                </Paper>
              ))}
            </div>
          )}
        </div>
      </PageBody>
    </div>
  );
};
